import { html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhPlus";
import "@phosphor-icons/webcomponents/PhUsers";
import "ui-components/empty-state/empty-state";
import "ui-components/button/wow-button";
import "ui-components/confirmation/wow-confirmation";

import { SignalElement, inject } from "ui-common/di";
import type { IRpcService } from "ui-common/services/rpc/types";
import { NotificationService } from "ui-components/notification";

import { styles } from "./messages-page.css";
import { t, translate } from "../../translations/i18n";
import { navigateTo } from "../../router";
import type { CreatorMessage } from "api-contracts/rpc/get-creator-messages";
import type { ICreatorMessagesState, ICreatorMessagesService } from "../../services/creator-messages/types";

import "../../components/page-content/page-content";
import "../../components/stat-badge/stat-badge";
import "../../components/messages-table/messages-table";
import "../../components/recent-message-banner/recent-message-banner";

type MessageFilter = "all" | "scheduled" | "sent" | "draft" | "archived";

@customElement("messages-page")
export class MessagesPage extends SignalElement {
  static override styles = styles;

  @inject<ICreatorMessagesState>("creatorMessagesState")
  private messagesState!: ICreatorMessagesState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @inject<ICreatorMessagesService>("creatorMessagesService")
  private messagesService!: ICreatorMessagesService;

  @state()
  private selectedFilter: MessageFilter = "all";

  @state()
  private archivingMessageId?: string;

  private getMostRecentMessage(messages: CreatorMessage[]): CreatorMessage | undefined {
    const sent = messages
      .filter((m) => m.status === "sent" || m.status === "sending")
      .sort((a, b) => new Date(b.sent_at!).getTime() - new Date(a.sent_at!).getTime());
    if (sent.length > 0) return sent[0];

    return [...messages].sort((a, b) => new Date(b.last_updated).getTime() - new Date(a.last_updated).getTime())[0];
  }

  private getFilteredMessages(
    messages: CreatorMessage[],
    filter: MessageFilter = this.selectedFilter,
  ): CreatorMessage[] {
    switch (filter) {
      case "draft":
        return messages.filter((m) => m.status === "draft");
      case "scheduled":
        return messages.filter((m) => m.status === "scheduled");
      case "sent":
        return messages.filter((m) => m.status === "sent" || m.status === "sending");
      case "archived":
        return messages.filter((m) => m.status === "archived");
      case "all":
      default:
        return messages;
    }
  }

  private getFilterCounts(messages: CreatorMessage[]) {
    return {
      all: messages.length,
      draft: messages.filter((m) => m.status === "draft").length,
      scheduled: messages.filter((m) => m.status === "scheduled").length,
      sent: messages.filter((m) => m.status === "sent" || m.status === "sending").length,
      archived: messages.filter((m) => m.status === "archived").length,
    };
  }

  private renderMainContent() {
    const isLoading = this.messagesState.isLoading.get();

    if (isLoading) {
      return html`
        <div class="main-content">
          <div class="loading-state">${t("messagesPage.loadingMessages")}</div>
        </div>
      `;
    }

    const messages = this.messagesState.messages.get();

    if (messages.length === 0) {
      return html`<div class="main-content">${this.renderEmptyState()}</div>`;
    }

    const counts = this.getFilterCounts(messages);
    const effectiveFilter =
      this.selectedFilter !== "all" && counts[this.selectedFilter] === 0 ? "all" : this.selectedFilter;
    const filteredMessages = this.getFilteredMessages(messages, effectiveFilter);
    const mostRecentMessage = this.getMostRecentMessage(messages);

    return html`
      <div class="main-content">
        ${mostRecentMessage
          ? html`
              <div class="recent-message-section">
                <h3 class="section-title">${t("messagesPage.mostRecentMessage")}</h3>
                <recent-message-banner
                  .message=${mostRecentMessage}
                  @edit-message=${this.handleEditMessage}
                  @view-message=${this.handleViewMessage}
                  @duplicate-message=${this.handleDuplicateMessage}
                  @archive-message=${this.handleArchiveMessage}
                ></recent-message-banner>
              </div>
            `
          : nothing}
        <div>
          <div class="messages-header">
            <h3 class="section-title">${t("messagesPage.myMessages")}</h3>
            <div class="messages-filter">
              <wow-button
                size="small"
                variant=${effectiveFilter === "all" ? "primary" : "subtle"}
                @click=${() => (this.selectedFilter = "all")}
              >
                ${t("messagesPage.all")} (${counts.all})
              </wow-button>
              ${counts.scheduled > 0
                ? html`
                    <wow-button
                      size="small"
                      variant=${effectiveFilter === "scheduled" ? "primary" : "subtle"}
                      @click=${() => (this.selectedFilter = "scheduled")}
                    >
                      ${t("messagesPage.scheduled")} (${counts.scheduled})
                    </wow-button>
                  `
                : nothing}
              ${counts.sent > 0
                ? html`
                    <wow-button
                      size="small"
                      variant=${effectiveFilter === "sent" ? "primary" : "subtle"}
                      @click=${() => (this.selectedFilter = "sent")}
                    >
                      ${t("messagesPage.sent")} (${counts.sent})
                    </wow-button>
                  `
                : nothing}
              ${counts.draft > 0
                ? html`
                    <wow-button
                      size="small"
                      variant=${effectiveFilter === "draft" ? "primary" : "subtle"}
                      @click=${() => (this.selectedFilter = "draft")}
                    >
                      ${t("messagesPage.draft")} (${counts.draft})
                    </wow-button>
                  `
                : nothing}
              ${counts.archived > 0
                ? html`
                    <wow-button
                      size="small"
                      variant=${effectiveFilter === "archived" ? "primary" : "subtle"}
                      @click=${() => (this.selectedFilter = "archived")}
                    >
                      ${t("messagesPage.archived")} (${counts.archived})
                    </wow-button>
                  `
                : nothing}
            </div>
          </div>
          <messages-table
            .messages=${filteredMessages}
            @edit-message=${this.handleEditMessage}
            @view-message=${this.handleViewMessage}
            @duplicate-message=${this.handleDuplicateMessage}
            @archive-message=${this.handleArchiveMessage}
          ></messages-table>
        </div>
      </div>
    `;
  }

  private renderEmptyState() {
    return html`
      <div>
        <h3 class="section-title">${t("messagesPage.myMessages")}</h3>
        <wow-empty-state
          .headline=${t("messagesPage.emptyStateHeadline")}
          .subtitle=${t("messagesPage.emptyStateSubtitle")}
          .buttonText=${t("messagesPage.createMessage")}
          @cta-click=${this.handleCreateMessage}
        >
          <ph-plus slot="icon" size="24"></ph-plus>
        </wow-empty-state>
      </div>
    `;
  }

  protected override render() {
    const communityCount = this.messagesState.communityCount.get();

    return html`
      <page-content .pageTitle=${t("messagesPage.pageTitle")}>
        <stat-badge slot="center" .count=${communityCount}>
          <ph-users size="18" slot="icon"></ph-users>
          ${t("messagesPage.totalCommunity")}
        </stat-badge>
        <div slot="actions">
          <wow-button variant="primary" size="small" @click=${this.handleCreateMessage}>
            <span class="create-icon"><ph-plus size="18"></ph-plus></span>
            <span class="create-full-label">${t("messagesPage.createMessage")}</span>
            <span class="create-short-label">${t("messagesPage.create")}</span>
          </wow-button>
        </div>
        <section class="containers">${this.renderMainContent()}</section>
      </page-content>

      ${this.archivingMessageId
        ? html`
            <wow-confirmation header=${translate("messagesPage.archiveMessage")}>
              <div class="delete-confirmation-content">
                <p>${t("messagesPage.archiveConfirmation")}</p>
                <div class="confirmation-buttons">
                  <wow-button variant="danger" size="medium" @click=${this.handleConfirmArchive}>
                    ${t("messagesPage.archiveMessage")}
                  </wow-button>
                  <wow-button variant="neutral" size="medium" @click=${this.handleCancelArchive}>
                    ${t("messagesPage.cancel")}
                  </wow-button>
                </div>
              </div>
            </wow-confirmation>
          `
        : nothing}
    `;
  }

  private handleCreateMessage() {
    navigateTo("messages/create", true);
  }

  private handleEditMessage(e: CustomEvent<{ message: CreatorMessage }>) {
    navigateTo("messages/edit", true, [e.detail.message.id]);
  }

  private handleViewMessage(e: CustomEvent<{ message: CreatorMessage }>) {
    navigateTo("messages/edit", true, [e.detail.message.id]);
  }

  private handleDuplicateMessage(e: CustomEvent<{ message: CreatorMessage }>) {
    const message = e.detail.message;
    this.rpcService
      .call("create-creator-message", {
        title: message.title,
        content: message.content,
        segments: message.segments,
        ctaEnabled: message.cta_enabled,
        ctaText: message.cta_text,
        ctaUrl: message.cta_url,
        imageFileId: message.image_file_id,
      })
      .then(async () => {
        this.messagesService.invalidateCache();
        await this.messagesService.loadMessages();
        NotificationService.success(translate("messagesPage.duplicateSuccess"));
      })
      .catch(() => {
        NotificationService.error(translate("messagesPage.duplicateError"));
      });
  }

  private handleArchiveMessage(e: CustomEvent<{ messageId: string }>) {
    this.archivingMessageId = e.detail.messageId;
  }

  private handleCancelArchive() {
    this.archivingMessageId = undefined;
  }

  private async handleConfirmArchive() {
    if (!this.archivingMessageId) return;

    try {
      await this.rpcService.call("archive-creator-message", { id: this.archivingMessageId });
      this.archivingMessageId = undefined;
      this.messagesService.clearCache();
      await this.messagesService.loadMessages();
      NotificationService.success(translate("messagesPage.archiveSuccess"));
    } catch {
      NotificationService.error(translate("messagesPage.archiveError"));
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "messages-page": MessagesPage;
  }
}
